import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Radio,
  RadioGroup,
  SimpleGrid,
  Switch,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { Link as RouteLink, useNavigate, useOutletContext, useParams } from 'react-router-dom';

import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { IOutletContext } from '../../../../../types';
import { IRecordedInterviewAssessmentPayload, RecordedInterviewAssessmentDecision } from 'types';
import { useCallback } from 'react';
import { assessRecordedInterview } from '../../../../../store/student/offer/actions';
import { getDisplayName } from '../../../../../utilities/formatting';
import { getFacialRecognitionScore } from '../../../../../utilities/scheduledInterviews';
import { FACIAL_RECOGNITION_SCORE_SCALE } from '../../../../../utilities/constants';
import { useTranslation } from "react-i18next";

type AssesmentFormPayload = {
  notes: { value: string };
  identityScore: { value: string };
  isReadingFromScript: { checked: boolean };
  isLipSyncing: { checked: boolean };
  decision: { value: RecordedInterviewAssessmentDecision };
  revealToUni: { checked: boolean };
};

export default function AssessInterview() {
    const { t } = useTranslation('common');
  const { auth, config } = useSelector((state: ApplicationState) => ({ auth: state.auth, config: state.config }));
  const { interview } = useOutletContext<IOutletContext>();
  const { facialRecognitionScore } = interview;

  const { interviewHash } = useParams();

  const identityScoreValue = getFacialRecognitionScore(facialRecognitionScore);
  const identityScore = typeof identityScoreValue !== 'number' ? 0 : identityScoreValue;
  const assessmentNote = interview.userAssessment?.assessmentNote ?? '';
  const decision = interview.userAssessment?.assessmentDecision ?? RecordedInterviewAssessmentDecision.None;
  const isReadingFromScript = interview.userAssessment?.isReadingFromScript ?? false;
  const isLipSyncing = interview.userAssessment?.isLipSyncing ?? false;
  const revealAssessmentToUni = interview.userAssessment?.revealAssessmentToUni ?? false;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSave: React.FormEventHandler<HTMLFormElement> = useCallback(
    event => {
      event.preventDefault();

      if (!interviewHash) {
        return;
      }

      const target = event.target as typeof event.target & AssesmentFormPayload;

      const payload: IRecordedInterviewAssessmentPayload = {
        assessmentNote: target.notes.value,
        // Keep facialRecognitionScore in 100-scale system
        facialRecognitionScore: parseFloat(target.identityScore.value) * FACIAL_RECOGNITION_SCORE_SCALE,
        isReadingFromScript: target.isReadingFromScript.checked,
        isLipSyncing: target.isLipSyncing.checked,
        assessedAt: Date.now(),
        assessedBy: getDisplayName(auth, config) ?? '',
        revealAssessmentToUni: target.revealToUni.checked,
        assessmentDecision: target.decision.value ?? RecordedInterviewAssessmentDecision.None,
      };

      dispatch(assessRecordedInterview({ interviewHash, payload }));
      navigate('..');
    },
    [auth, config, dispatch, interviewHash, navigate],
  );

  return (
    <Box maxWidth="lg">
      <form onSubmit={onSave}>
        <Flex mb="3">
          <Button
            as={RouteLink}
            to={'..'}
            variant="ghost"
            ml="-3.5"
            leftIcon={<FontAwesomeIcon className="utility-icon" icon={regular('arrow-left')} />}
          >
            {t('common:allQuestions')}
                                </Button>
        </Flex>

        <Heading as="h3" fontSize="2xl" mb="4">
          {t('common:assessInterview')}
                          </Heading>

        <VStack spacing="8">
          <AssessorNotes assessmentNote={assessmentNote} />

          <IdentityScore identityScore={identityScore} />

          <CheatingFlags isReadingFromScript={isReadingFromScript} isLipSyncing={isLipSyncing} />

          <AssessmentDecision decision={decision} />

          <RevialToUni revealAssessmentToUni={revealAssessmentToUni} />
        </VStack>

        <Flex>
          <ButtonGroup ml="auto" mt="6">
            <Button as={RouteLink} to={'..'} ml="auto" size="lg" variant="secondary" mr={3}>
              {t('common:cancel')}
                                      </Button>

            <Button type="submit" size="lg" variant="solid">
              {t('common:save')}
                                      </Button>
          </ButtonGroup>
        </Flex>
      </form>
    </Box>
  );
}

function RevialToUni({ revealAssessmentToUni }: { revealAssessmentToUni: boolean }) {
    const { t } = useTranslation('common');
  return (
    <FormControl display="flex" alignItems="center">
      <FormLabel htmlFor="reveal-to-university" mb="0">
        {t('common:revealToUniversity')}
                    </FormLabel>

      <Switch name="revealToUni" id="reveal-to-university" defaultChecked={revealAssessmentToUni} />
    </FormControl>
  );
}

function AssessmentDecision({ decision }: { decision: RecordedInterviewAssessmentDecision }) {
    const { t } = useTranslation('common');
  return (
    <FormControl as="fieldset" border="none">
      <FormLabel as="legend">{t('common:suggestDecision')}</FormLabel>

      <RadioGroup name="decision" defaultValue={decision}>
        <SimpleGrid columns={4} spacing="4">
          <Radio value={RecordedInterviewAssessmentDecision.None}>{t('common:none')}</Radio>
          <Radio value={RecordedInterviewAssessmentDecision.Pass}>{t('common:pass')}</Radio>
          <Radio value={RecordedInterviewAssessmentDecision.Resit}>{t('common:resit')}</Radio>
          <Radio value={RecordedInterviewAssessmentDecision.Fail}>{t('common:fail')}</Radio>
        </SimpleGrid>
      </RadioGroup>
    </FormControl>
  );
}

function CheatingFlags({ isLipSyncing, isReadingFromScript }: { isReadingFromScript: boolean; isLipSyncing: boolean }) {
    const { t } = useTranslation('common');
  return (
    <FormControl as="fieldset" border="none">
      <FormLabel as="legend">{t('common:flagConcerns')}</FormLabel>

      <VStack alignItems="stretch" spacing="0">
        <Checkbox name="isReadingFromScript" defaultChecked={isReadingFromScript}>
          {t('common:readingFromAScript')}
                          </Checkbox>
        <Checkbox name="isLipSyncing" defaultChecked={isLipSyncing}>
          {t('common:lipSyncing')}
                          </Checkbox>
      </VStack>
    </FormControl>
  );
}

function IdentityScore({ identityScore }: { identityScore: number }) {
    const { t } = useTranslation('common');
  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    // avoiding numric (e).
    const value = e.target?.value?.match(/[0-9]/g);
    if (value) {
      e.target.value = value.join('');
    } else {
      e.target.value = '';
    }
  };
  return (
    <FormControl>
      <FormLabel>{t('common:identityScore')}</FormLabel>

      <NumberInput name="identityScore" defaultValue={identityScore} min={0} max={9.9} step={0.1}>
        <NumberInputField type="text" inputMode="numeric" onChange={onChangeHandler} />
        <NumberInputStepper>
          <NumberIncrementStepper />
          <NumberDecrementStepper />
        </NumberInputStepper>
      </NumberInput>
    </FormControl>
  );
}

function AssessorNotes({ assessmentNote }: { assessmentNote: string }) {
    const { t } = useTranslation('common');
  return (
    <FormControl>
      <FormLabel>{t('common:assessorNotes')}</FormLabel>

      <Textarea defaultValue={assessmentNote} name="notes" placeholder={t('common:addNotes')} />
    </FormControl>
  );
}
